import { useEffect, useRef } from 'react'
import {
  beaconLeaveWaitingRoom,
  heartbeatWaitingRoom,
  joinWaitingRoom,
  leaveWaitingRoom,
} from '../utilities/api'
import {
  clearWaitingRoomToken,
  getWaitingRoomToken,
  setWaitingRoomToken,
} from '../utilities/waitingRoom'

const HEARTBEAT_INTERVAL_MS = 60 * 1000

export default function useWaitingRoomHeartbeat(eventUuid, enabled = true) {
  const intervalRef = useRef(null)
  const tokenRef = useRef(null)

  useEffect(() => {
    if (!enabled || !eventUuid) return undefined

    const initialToken = getWaitingRoomToken(eventUuid)
    if (!initialToken) return undefined

    tokenRef.current = initialToken
    let cancelled = false
    let rejoinInFlight = null

    // A slot only goes stale after the tab stops beating for it, which happens
    // whenever a shopper wanders off the event and comes back. The stored token
    // still looks fine to the client, so this heartbeat is the first thing to
    // notice. Reclaim the slot here instead of letting their next cart action
    // fail and bounce them through the queue for a seat that is sitting free.
    const rejoin = () => {
      if (rejoinInFlight) return rejoinInFlight

      rejoinInFlight = joinWaitingRoom(eventUuid)
        .then((res) => {
          if (cancelled) return
          const { status, token } = res.data || {}
          if (status === 'admitted' && token) {
            tokenRef.current = token
            setWaitingRoomToken(eventUuid, token)
            return
          }
          // Genuinely back in line (or the room was turned off). Drop the dead
          // token so the purchase flow routes them to the waiting room.
          clearWaitingRoomToken(eventUuid)
        })
        .catch((err) => {
          console.error('Waiting room rejoin failed', err)
          if (!cancelled) clearWaitingRoomToken(eventUuid)
        })
        .finally(() => {
          rejoinInFlight = null
        })

      return rejoinInFlight
    }

    const sendHeartbeat = () => {
      const token = tokenRef.current
      if (!token) return

      heartbeatWaitingRoom(eventUuid, token).catch((err) => {
        console.error('Waiting room heartbeat failed', err)
        if (
          err?.response?.status === 403 ||
          err?.response?.data?.code === 'WAITING_ROOM_REQUIRED'
        ) {
          tokenRef.current = null
          rejoin()
        }
      })
    }

    sendHeartbeat()
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS)

    // Releasing the slot on unload is what lets the next fan in. It is a soft
    // release because this also fires on refresh, where the heartbeat above
    // reclaims the slot as soon as the page comes back.
    const onPageHide = (event) => {
      // persisted means the page went into the back/forward cache and is still
      // alive, so leave that slot to the heartbeat grace period instead.
      if (event?.persisted) return
      if (!tokenRef.current) return
      beaconLeaveWaitingRoom(eventUuid, tokenRef.current, { soft: true })
    }

    const onResume = () => {
      if (document.visibilityState !== 'visible') return
      // Coming back to a backgrounded tab is the most common way to find a
      // dropped slot, so recover it rather than only beating an old token.
      if (tokenRef.current) {
        sendHeartbeat()
      } else {
        rejoin()
      }
    }

    window.addEventListener('pagehide', onPageHide)
    window.addEventListener('pageshow', onResume)
    document.addEventListener('visibilitychange', onResume)

    return () => {
      cancelled = true
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onResume)
      document.removeEventListener('visibilitychange', onResume)
    }
  }, [eventUuid, enabled])

  return {
    leave: async () => {
      if (!eventUuid) return
      const token = getWaitingRoomToken(eventUuid)
      if (!token) return
      try {
        await leaveWaitingRoom(eventUuid, token)
      } catch (err) {
        console.error('Waiting room leave failed', err)
      }
    },
  }
}
